import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'
import type { VideoCardProps, VideoCardStatusTone } from '../model'

const videoCardTokens = designTokens.components.videoCard

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.base),
}

const metaStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  opacity: 0.6,
  ...typographyStyle(designTokens.typography.body.smTight, {
    fontSize: '10px',
    lineHeight: 'normal',
  }),
}

const descriptionStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  opacity: 0.7,
  ...typographyStyle(designTokens.typography.body.sm, {
    lineHeight: '1.2',
  }),
}

const timeBadgeTextStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.xs, {
    fontSize: '8px',
    lineHeight: 'normal',
  }),
}

const statusBadgeTextStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.baseSemibold, {
    fontSize: videoCardTokens.statusBadge.fontSize,
    lineHeight: 'normal',
  }),
}

const statusToneTokens: Record<
  VideoCardStatusTone,
  {
    background: string
    text: string
  }
> = {
  orange: {
    background: videoCardTokens.statusBadge.toneOrangeBackground,
    text: videoCardTokens.statusBadge.toneOrangeText,
  },
  purple: {
    background: videoCardTokens.statusBadge.tonePurpleBackground,
    text: videoCardTokens.statusBadge.tonePurpleText,
  },
  blue: {
    background: videoCardTokens.statusBadge.toneBlueBackground,
    text: videoCardTokens.statusBadge.toneBlueText,
  },
  pink: {
    background: videoCardTokens.statusBadge.tonePinkBackground,
    text: videoCardTokens.statusBadge.tonePinkText,
  },
}

const BookmarkIcon = ({ color }: { color: string }) => (
  <svg
    width={videoCardTokens.bookmarkButton.iconSize}
    height={videoCardTokens.bookmarkButton.iconSize}
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    focusable="false"
    aria-hidden="true"
  >
    <path
      d="M3.125 4.375C3.125 2.99429 4.24429 1.875 5.625 1.875H9.375C10.7557 1.875 11.875 2.99429 11.875 4.375V12.6052C11.875 13.0997 11.328 13.3983 10.912 13.1309L8.17595 11.372C7.76419 11.1073 7.23581 11.1073 6.82405 11.372L4.08797 13.1309C3.67203 13.3983 3.125 13.0997 3.125 12.6052V4.375Z"
      stroke={color}
      strokeWidth="0.9375"
    />
    <path
      d="M5.625 5.3125H9.375"
      stroke={color}
      strokeWidth="0.9375"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

BookmarkIcon.displayName = 'BookmarkIcon'

const LockIcon = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    focusable="false"
    aria-hidden="true"
  >
    <rect x="2.5" y="7.5" width="11" height="6.5" rx="2" stroke="#FEFEFE" strokeWidth="1" />
    <path
      d="M5 7.5V5.8C5 4.25 6.25 3 7.8 3H8.2C9.75 3 11 4.25 11 5.8V7.5"
      stroke="#FEFEFE"
      strokeWidth="1"
      strokeLinecap="round"
    />
  </svg>
)

LockIcon.displayName = 'LockIcon'

export function VideoCard({
  title,
  shortDescription,
  coverSrc,
  coverAlt = '',
  contentTypeLabel,
  contentCountLabel,
  durationMetaLabel,
  remainingLabel,
  durationLabel,
  statusBadge,
  accessState = 'available',
  isBookmarked = false,
  isBookmarkPending = false,
  isSelected = false,
  onBookmarkToggle,
  onHoverStart,
  onClick,
  className,
}: VideoCardProps) {
  const statusTone = statusBadge?.tone ?? 'orange'
  const statusStyle = statusToneTokens[statusTone]
  const isClickable = typeof onClick === 'function'
  const isLocked = accessState === 'locked'
  const normalizedShortDescription = shortDescription?.trim() ?? ''

  return (
    <div
      className={cn(
        'flex shrink-0 overflow-visible',
        isClickable ? 'cursor-pointer' : null,
        className
      )}
      style={{
        width: videoCardTokens.width,
        minHeight: videoCardTokens.height,
        borderRadius: videoCardTokens.radius,
      }}
      onClick={onClick}
      onMouseEnter={onHoverStart}
      onFocus={onHoverStart}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') {
                return
              }
              event.preventDefault()
              onClick?.()
            }
          : undefined
      }
    >
      <div
        className="relative flex h-full w-full flex-col overflow-hidden"
        style={{
          borderRadius: videoCardTokens.radius,
          backgroundColor: designTokens.colors.neutral.grey,
        }}
      >
        {isSelected ? (
          <div
            className="pointer-events-none absolute inset-0 z-10"
            style={{
              borderRadius: videoCardTokens.radius,
              boxShadow: 'inset 0 0 0 2px #A885FF',
            }}
            aria-hidden="true"
          />
        ) : null}
        <div
          className="relative w-full overflow-hidden"
          style={{
            aspectRatio: '16 / 9',
            backgroundColor: designTokens.colors.background.surface,
          }}
        >
          <img src={coverSrc} alt={coverAlt} className="h-full w-full object-cover" />
          {isLocked ? <div className="absolute inset-0 bg-black/35" aria-hidden="true" /> : null}
          <button
            type="button"
            aria-label={
              isBookmarkPending
                ? 'Updating bookmark'
                : isBookmarked
                  ? 'Remove bookmark'
                  : 'Add bookmark'
            }
            className="absolute top-1.5 right-1.5 inline-flex cursor-pointer items-center justify-center"
            disabled={isBookmarkPending}
            aria-busy={isBookmarkPending}
            style={{
              width: videoCardTokens.bookmarkButton.size,
              height: videoCardTokens.bookmarkButton.size,
              borderRadius: videoCardTokens.bookmarkButton.radius,
              padding: videoCardTokens.bookmarkButton.padding,
              borderWidth: designTokens.borders.widths.thin,
              borderStyle: designTokens.borders.styles.solid,
              backgroundColor: isBookmarked
                ? videoCardTokens.bookmarkButton.activeBackground
                : videoCardTokens.bookmarkButton.inactiveBackground,
              borderColor: isBookmarked
                ? videoCardTokens.bookmarkButton.activeBorder
                : videoCardTokens.bookmarkButton.inactiveBorder,
              opacity: isBookmarkPending ? 0.72 : 1,
            }}
            onClick={(event) => {
              event.stopPropagation()
              onBookmarkToggle?.()
            }}
          >
            <BookmarkIcon
              color={
                isBookmarked
                  ? videoCardTokens.bookmarkButton.activeIcon
                  : videoCardTokens.bookmarkButton.inactiveIcon
              }
            />
          </button>
          {isLocked && !statusBadge ? (
            <div className="absolute left-1 bottom-1 inline-flex items-center justify-center gap-1 rounded-[4px] bg-black/65 px-1.5 py-0.5">
              <LockIcon />
              <span style={timeBadgeTextStyle}>закрыто</span>
            </div>
          ) : null}
          {remainingLabel || durationLabel ? (
            <div
              className="absolute right-1 bottom-1 flex items-center justify-end"
              style={{ gap: designTokens.spacing.xs }}
            >
              {remainingLabel ? (
                <div
                  className="inline-flex items-center justify-center"
                  style={{
                    borderRadius: videoCardTokens.timeBadge.radius,
                    padding: `${videoCardTokens.timeBadge.paddingY} ${videoCardTokens.timeBadge.paddingX}`,
                    backgroundColor: videoCardTokens.timeBadge.background,
                  }}
                >
                  <span style={timeBadgeTextStyle}>{remainingLabel}</span>
                </div>
              ) : null}
              {durationLabel ? (
                <div
                  className="inline-flex items-center justify-center"
                  style={{
                    borderRadius: videoCardTokens.timeBadge.radius,
                    padding: `${videoCardTokens.timeBadge.paddingY} ${videoCardTokens.timeBadge.paddingX}`,
                    backgroundColor: videoCardTokens.timeBadge.background,
                  }}
                >
                  <span style={timeBadgeTextStyle}>{durationLabel}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div
          className="flex w-full flex-col"
          style={{
            minHeight: videoCardTokens.bodyHeight,
            gap: videoCardTokens.bodyGap,
            padding: `${videoCardTokens.bodyPaddingY} ${videoCardTokens.bodyPaddingX}`,
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center" style={{ gap: videoCardTokens.metaGap }}>
              <span style={metaStyle}>{contentTypeLabel}</span>
              {contentCountLabel ? (
                <>
                  <span style={metaStyle}>•</span>
                  <span style={metaStyle}>{contentCountLabel}</span>
                </>
              ) : null}
              {durationMetaLabel ? (
                <>
                  <span style={metaStyle}>•</span>
                  <span style={metaStyle}>{durationMetaLabel}</span>
                </>
              ) : null}
            </div>
            {statusBadge ? (
              <div
                className="inline-flex items-center justify-center"
                style={{
                  borderRadius: videoCardTokens.statusBadge.radius,
                  padding: `${videoCardTokens.statusBadge.paddingY} ${videoCardTokens.statusBadge.paddingX}`,
                  backgroundColor: statusStyle.background,
                }}
              >
                <span style={{ ...statusBadgeTextStyle, color: statusStyle.text }}>
                  {statusBadge.label}
                </span>
              </div>
            ) : null}
          </div>
          <h3 style={titleStyle} className="line-clamp-3">
            {title}
          </h3>
          {normalizedShortDescription ? (
            <p style={descriptionStyle} className="line-clamp-1">
              {normalizedShortDescription}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
